// The DE2 blocks that can carry an AI suggestion, in the order given — pass
// `dataExtractionViewModel.blocks` for template order.
//
// Only the gates that depend on the template alone live here; the options fit
// and the form value are answered per completing action.

import { isFeatureEnabledForField } from 'concepts/AutoDocExtraction/fieldFlags';
import { SuggestibleField } from 'concepts/AutoDocExtraction/outstandingSuggestions';
import { TemplateBlock } from 'concepts/Extraction/Blocks/BaseBlock';
import {
  TextFieldBlock,
  blockIsTextField,
} from 'concepts/Extraction/Blocks/TextField/TextFieldBlock';
import {
  MultipleChoiceBlock,
  blockIsMultipleChoice,
} from 'concepts/Extraction/Blocks/MultipleChoice/MultipleChoiceBlock';
import {
  CheckboxesBlock,
  blockIsCheckboxes,
} from 'concepts/Extraction/Blocks/Checkboxes/CheckboxesBlock';

// The three block types with a Suggestible control.
export type SuggestibleBlock =
  | TextFieldBlock
  | MultipleChoiceBlock
  | CheckboxesBlock;

export type DE2SuggestibleField = SuggestibleField & {
  // Kept so later gates can match options and read the form value by block id.
  block: SuggestibleBlock;
};

type DE2SuggestibleFieldsArgs = {
  blocks: TemplateBlock[];
  hasFeature: (feature: string) => boolean;
};

const isSuggestibleBlock = (block: TemplateBlock): block is SuggestibleBlock =>
  blockIsTextField(block) ||
  blockIsMultipleChoice(block) ||
  blockIsCheckboxes(block);

export const de2SuggestibleFields = ({
  blocks,
  hasFeature,
}: DE2SuggestibleFieldsArgs): DE2SuggestibleField[] =>
  blocks.flatMap((block) => {
    const { autoDocExtField } = block;

    if (!autoDocExtField) return [];
    if (!isSuggestibleBlock(block)) return [];
    if (!isFeatureEnabledForField(autoDocExtField, hasFeature)) return [];

    return [
      {
        autoDocExtField,
        // Two blocks can share an autoDocExtField, each with its own control.
        statusKey: block.id,
        label: block.label,
        block,
      },
    ];
  });
